import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * A reusable options dropdown button component.
 *
 * @param {Array} options - Array of menu options, each containing a `label` and `onClick` handler.
 * @param {string} className - Optional additional class for the wrapper div.
 * @param {string} buttonClass - Optional additional class for the button.
 * @param {string} menuClass - Optional additional class for the dropdown menu.
 */
const OptionsButton = ({ options = [], className = '', buttonClass = '', menuClass = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  /**
   * Toggles the dropdown menu open/closed state.
   */
  const toggleMenu = useCallback(() => setIsOpen((prev) => !prev), []);

  /**
   * Handles clicks outside of the dropdown to close it.
   * Handles the 'Escape' key press to close the menu.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      {/* Button to open the options menu */}
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={`p-2 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none ${buttonClass}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="text-gray-600 text-xl">⋯</span>
      </button>

      {/* Dropdown menu - Render it in a portal */}
      {isOpen &&
        createPortal(
          <div
            className={`absolute mt-2 w-32 bg-white rounded-lg shadow-lg py-2 border border-gray-200 z-[100] ${menuClass}`}
            role="menu"
            style={{
              top: `${buttonPosition.top + 30}px`,
              left: `${buttonPosition.left - 90}px`,
            }}
            ref={dropdownRef}
          >
            {options.map(({ label, onClick }, index) => (
              <OptionItem
                key={index}
                label={label}
                onClick={() => {
                  onClick();
                  setIsOpen(false);
                }}
              />
            ))}
          </div>,
          document.getElementById('dropdown-portal-root'),
        )}
    </div>
  );
};

/**
 * A single menu item inside the dropdown.
 *
 * @param {string} label - The text to display for the option.
 * @param {Function} onClick - Function to execute when the option is clicked.
 */
const OptionItem = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
    role="menuitem"
  >
    {label}
  </button>
);

export default OptionsButton;
